import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, FileText, CheckCircle2, DollarSign, MapPin, Calendar, ChevronDown, ChevronRight, Zap, Car } from 'lucide-react';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DetalheItem {
  id: string;
  cliente: string;
  valor: number;
  data: string;
  status?: string;
  modelo?: string;
}

interface ParceiroRedeMetricas {
  totalLeads: number;
  totalPropostas: number;
  totalContratos: number;
  valorTotalPropostas: number;
  valorTotalContratos: number;
  detalhes?: {
    fotovoltaico: {
      totalPropostas: number;
      valorTotalPropostas: number;
      totalContratos: number;
      valorTotalContratos: number;
      listaPropostas?: DetalheItem[];
      listaContratos?: DetalheItem[];
    };
    carregadores: {
      totalPropostas: number;
      valorTotalPropostas: number;
      totalContratos: number;
      valorTotalContratos: number;
      listaPropostas?: DetalheItem[];
      listaContratos?: DetalheItem[];
    };
  };
}

interface ParceiroRede {
  id: string;
  nome: string;
  cidade: string | null;
  estado: string | null;
  created_at: string;
  ativo: boolean;
  metricas: ParceiroRedeMetricas;
}

interface ParceiroRedeData {
  totalParceiros: number;
  parceiros: ParceiroRede[];
}

interface ParceiroRedeTabProps {
  parceiroId: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export function ParceiroRedeTab({ parceiroId }: ParceiroRedeTabProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redeData, setRedeData] = useState<ParceiroRedeData | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  useEffect(() => {
    async function loadRede() {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetchWithAuth(`/api/parceiros/${parceiroId}/rede`);
        
        if (!response.ok) {
          throw new Error('Erro ao carregar rede do parceiro');
        }
        
        const data = await response.json();
        setRedeData(data);
      } catch (err) {
        console.error('Erro ao carregar rede:', err);
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
      } finally {
        setLoading(false);
      }
    }

    if (parceiroId) {
      loadRede();
    }
  }, [parceiroId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!redeData || redeData.totalParceiros === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            Este parceiro ainda não possui parceiros cadastrados em sua rede.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Calcular totais agregados
  const totaisAgregados = redeData.parceiros.reduce(
    (acc, parceiro) => ({
      totalLeads: acc.totalLeads + parceiro.metricas.totalLeads,
      totalPropostas: acc.totalPropostas + parceiro.metricas.totalPropostas,
      totalContratos: acc.totalContratos + parceiro.metricas.totalContratos,
      valorTotalPropostas: acc.valorTotalPropostas + parceiro.metricas.valorTotalPropostas,
      valorTotalContratos: acc.valorTotalContratos + parceiro.metricas.valorTotalContratos
    }),
    {
      totalLeads: 0,
      totalPropostas: 0,
      totalContratos: 0,
      valorTotalPropostas: 0,
      valorTotalContratos: 0
    }
  );

  return (
    <div className="space-y-6">
      {/* Resumo da Rede */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 w-full">
        <Card className="flex-auto min-w-0">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground whitespace-nowrap mb-2">
              Total de Parceiros
            </p>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xl font-bold text-blue-600 whitespace-nowrap">
                {redeData.totalParceiros}
              </div>
              <Users className="h-6 w-6 text-blue-600/20 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="flex-auto min-w-0">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground whitespace-nowrap mb-2">
              Leads Gerados
            </p>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xl font-bold text-green-600 whitespace-nowrap">
                {totaisAgregados.totalLeads}
              </div>
              <Users className="h-6 w-6 text-green-600/20 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="flex-auto min-w-0">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground whitespace-nowrap mb-2">
              Propostas Geradas
            </p>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xl font-bold text-purple-600 whitespace-nowrap">
                {totaisAgregados.totalPropostas}
              </div>
              <FileText className="h-6 w-6 text-purple-600/20 flex-shrink-0" />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 whitespace-nowrap truncate">
              {formatCurrency(totaisAgregados.valorTotalPropostas)}
            </p>
          </CardContent>
        </Card>

        <Card className="flex-auto min-w-0">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground whitespace-nowrap mb-2">
              Contratos Fechados
            </p>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xl font-bold text-orange-600 whitespace-nowrap">
                {totaisAgregados.totalContratos}
              </div>
              <CheckCircle2 className="h-6 w-6 text-orange-600/20 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="flex-auto min-w-0">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground whitespace-nowrap mb-2">
              Valor em Contratos
            </p>
            <div className="flex items-center justify-between gap-2">
              <div className="text-lg font-bold text-red-600 whitespace-nowrap">
                {formatCurrency(totaisAgregados.valorTotalContratos)}
              </div>
              <DollarSign className="h-6 w-6 text-red-600/20 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Parceiros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Parceiros da Rede</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Parceiro</TableHead>
                  <TableHead>Localização</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Leads</TableHead>
                  <TableHead className="text-right">Propostas</TableHead>
                  <TableHead className="text-right">Contratos</TableHead>
                  <TableHead className="text-right">Valor Contratos</TableHead>
                  <TableHead>Data Cadastro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redeData.parceiros.map((parceiro) => {
                  const isExpanded = expandedRows.has(parceiro.id);
                  const hasDetails = (parceiro.metricas.totalPropostas > 0 || parceiro.metricas.totalContratos > 0);

                  return (
                    <>
                      <TableRow 
                        key={parceiro.id}
                        className={cn(
                          "cursor-pointer hover:bg-muted/50 transition-colors",
                          isExpanded && "bg-muted/30"
                        )}
                        onClick={() => hasDetails && toggleRow(parceiro.id)}
                      >
                        <TableCell>
                          {hasDetails && (
                            isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {parceiro.nome}
                        </TableCell>
                        <TableCell>
                          {parceiro.cidade && parceiro.estado ? (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {parceiro.cidade}/{parceiro.estado}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Não informado</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={parceiro.ativo ? 'default' : 'secondary'}>
                            {parceiro.ativo ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-blue-600">
                          {parceiro.metricas.totalLeads}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-semibold text-purple-600">
                            {parceiro.metricas.totalPropostas}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatCurrency(parceiro.metricas.valorTotalPropostas)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-semibold text-orange-600">
                            {parceiro.metricas.totalContratos}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-semibold text-red-600">
                            {formatCurrency(parceiro.metricas.valorTotalContratos)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {formatDate(parceiro.created_at)}
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {isExpanded && hasDetails && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20 border-b">
                          <TableCell colSpan={9} className="p-0">
                            <div className="p-4 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                              {/* Seção Fotovoltaico */}
                              {(parceiro.metricas.detalhes?.fotovoltaico.totalPropostas ?? 0) > 0 || (parceiro.metricas.detalhes?.fotovoltaico.totalContratos ?? 0) > 0 ? (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2 text-sm font-semibold text-primary border-b pb-1">
                                    <Zap className="h-4 w-4" />
                                    Fotovoltaico
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Propostas FV */}
                                    <div className="space-y-2">
                                      <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Propostas ({parceiro.metricas.detalhes?.fotovoltaico.totalPropostas})</h5>
                                      <div className="bg-background rounded-md border overflow-hidden">
                                        <table className="w-full text-xs">
                                          <thead className="bg-muted/50 border-b">
                                            <tr>
                                              <th className="text-left p-2">Cliente</th>
                                              <th className="text-right p-2">Valor</th>
                                              <th className="text-right p-2">Data</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y">
                                            {parceiro.metricas.detalhes?.fotovoltaico.listaPropostas?.map((item) => (
                                              <tr key={item.id}>
                                                <td className="p-2 font-medium">{item.cliente}</td>
                                                <td className="p-2 text-right">{formatCurrency(item.valor)}</td>
                                                <td className="p-2 text-right text-muted-foreground">{formatDate(item.data)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>

                                    {/* Contratos FV */}
                                    <div className="space-y-2">
                                      <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Contratos ({parceiro.metricas.detalhes?.fotovoltaico.totalContratos})</h5>
                                      <div className="bg-background rounded-md border overflow-hidden">
                                        <table className="w-full text-xs">
                                          <thead className="bg-muted/50 border-b">
                                            <tr>
                                              <th className="text-left p-2">Cliente</th>
                                              <th className="text-right p-2">Valor</th>
                                              <th className="text-right p-2">Status</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y">
                                            {parceiro.metricas.detalhes?.fotovoltaico.listaContratos?.map((item) => (
                                              <tr key={item.id}>
                                                <td className="p-2 font-medium">{item.cliente}</td>
                                                <td className="p-2 text-right">{formatCurrency(item.valor)}</td>
                                                <td className="p-2 text-right">
                                                  <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize">
                                                    {item.status?.replace('_', ' ')}
                                                  </Badge>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}

                              {/* Seção Carregadores */}
                              {(parceiro.metricas.detalhes?.carregadores.totalPropostas ?? 0) > 0 || (parceiro.metricas.detalhes?.carregadores.totalContratos ?? 0) > 0 ? (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2 text-sm font-semibold text-blue-600 border-b pb-1">
                                    <Car className="h-4 w-4" />
                                    Carregadores Veiculares
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Propostas EV */}
                                    <div className="space-y-2">
                                      <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Propostas ({parceiro.metricas.detalhes?.carregadores.totalPropostas})</h5>
                                      <div className="bg-background rounded-md border overflow-hidden">
                                        <table className="w-full text-xs">
                                          <thead className="bg-muted/50 border-b">
                                            <tr>
                                              <th className="text-left p-2">Cliente</th>
                                              <th className="text-left p-2">Modelo</th>
                                              <th className="text-right p-2">Valor</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y">
                                            {parceiro.metricas.detalhes?.carregadores.listaPropostas?.map((item) => (
                                              <tr key={item.id}>
                                                <td className="p-2 font-medium">{item.cliente}</td>
                                                <td className="p-2 text-muted-foreground">{item.modelo}</td>
                                                <td className="p-2 text-right">{formatCurrency(item.valor)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>

                                    {/* Contratos EV */}
                                    <div className="space-y-2">
                                      <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Contratos ({parceiro.metricas.detalhes?.carregadores.totalContratos})</h5>
                                      <div className="bg-background rounded-md border overflow-hidden">
                                        <table className="w-full text-xs">
                                          <thead className="bg-muted/50 border-b">
                                            <tr>
                                              <th className="text-left p-2">Cliente</th>
                                              <th className="text-left p-2">Modelo</th>
                                              <th className="text-right p-2">Valor</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y">
                                            {parceiro.metricas.detalhes?.carregadores.listaContratos?.map((item) => (
                                              <tr key={item.id}>
                                                <td className="p-2 font-medium">{item.cliente}</td>
                                                <td className="p-2 text-muted-foreground">{item.modelo}</td>
                                                <td className="p-2 text-right">{formatCurrency(item.valor)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

